import { ChevronRight, MapPin, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Badge } from '@/components/ui';
import type { MapEntity } from '@/types/api';

interface Props {
  entities: MapEntity[];
  /** Lat / lon (matches every entry; rendered as a short header subtitle). */
  lat: number;
  lon: number;
  onSelect: (entity: MapEntity) => void;
  onClose: () => void;
}

/**
 * List view shown when several entities share the exact (rounded) map
 * coordinate. Clicking a row flips the parent into the single-entity
 * popup focused on that IRI.
 */
export function MapClusterPopup({ entities, lat, lon, onSelect, onClose }: Props) {
  const { t } = useTranslation(['app', 'common']);
  return (
    <div className="min-w-[280px] max-w-[360px] space-y-2 p-1">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <p className="flex items-center gap-1 text-h4 text-slate-900">
            <MapPin className="h-3.5 w-3.5 text-accent" aria-hidden />
            {t('app:map.cluster.title', {
              n: entities.length,
              defaultValue: '{{n}} entities at this location',
            })}
          </p>
          <p className="mt-0.5 font-mono text-[10px] text-slate-500">
            {lat.toFixed(4)}, {lon.toFixed(4)}
          </p>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="rounded p-0.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
          aria-label={t('common:actions.close', 'Close')}
        >
          <X className="h-3.5 w-3.5" aria-hidden />
        </button>
      </div>
      <ul className="max-h-[280px] divide-y divide-slate-100 overflow-y-auto rounded border border-slate-200">
        {entities.map((e) => (
          <li key={e.iri}>
            <button
              type="button"
              onClick={(ev) => {
                // Keep the click inside the popup — maplibre's
                // closeOnClick is disabled, but stopping propagation
                // here also prevents any future map-level handler
                // from swallowing the navigation.
                ev.stopPropagation();
                onSelect(e);
              }}
              className="flex w-full items-center gap-2 px-2 py-1.5 text-left text-small transition-colors hover:bg-slate-50"
            >
              <span className="min-w-0 flex-1 truncate text-slate-900" title={e.label}>
                {e.label}
              </span>
              <Badge variant="neutral" className="text-[10px]">
                {e.className}
              </Badge>
              <ChevronRight
                className="h-3.5 w-3.5 shrink-0 text-slate-400"
                aria-hidden
              />
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}
